/**
 * InlineAddSearch — the affordance behind every "+ Add" chip on the
 * HouseholdProduct settings surface (T12.1.7.1). Tapping the chip reveals a
 * `.setsearch`-styled row that filters a list of options; picking one calls
 * `onPick`. Replaces the legacy `<select>` + "Add" `<Button>` add rows.
 *
 * Kept deliberately small: it reuses the existing brand / retailer / store
 * option arrays the settings component already derives — no new data fetch.
 *
 * Styles flow from tokens (CP5): no hex, no raw px.
 */

import { useMemo, useState, type CSSProperties } from 'react';
import {
  tagAddStyle,
  setsearchStyle,
  setsearchInputStyle,
  presultboxStyle,
  presultStyle,
  presultMetaStyle,
  presultNmStyle,
  miniaddSubStyle,
  mutedStyle,
} from './settingsStyles';

export interface AddOption {
  id: string;
  label: string;
}

const wrapStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
};

export function InlineAddSearch({
  label,
  options,
  onPick,
  testId,
  emptyText = 'Nothing left to add.',
}: {
  /** Chip text, e.g. "+ Add brand". */
  label: string;
  /** The selectable options (already filtered to "not yet linked"). */
  options: AddOption[];
  onPick: (id: string) => void;
  /** data-testid stem; the chip is `{testId}-add`, the input `{testId}-input`. */
  testId: string;
  emptyText?: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');

  const filtered = useMemo(() => {
    const t = term.trim().toLowerCase();
    if (!t) return options;
    return options.filter((o) => o.label.toLowerCase().includes(t));
  }, [options, term]);

  if (!open) {
    return (
      <span
        role="button"
        tabIndex={0}
        style={tagAddStyle}
        onClick={() => setOpen(true)}
        data-testid={`${testId}-add`}
      >
        {label}
      </span>
    );
  }

  return (
    <div style={wrapStyle} data-testid={`${testId}-panel`}>
      <div className="field-focus-ring" style={setsearchStyle}>
        <span aria-hidden="true" style={{ color: 'var(--color-text-muted)' }}>
          🔍
        </span>
        <input
          style={setsearchInputStyle}
          aria-label={label}
          autoFocus
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Type to filter…"
          data-testid={`${testId}-input`}
        />
        <span
          role="button"
          tabIndex={0}
          aria-label="Cancel"
          style={{ ...mutedStyle, cursor: 'pointer' }}
          onClick={() => {
            setOpen(false);
            setTerm('');
          }}
          data-testid={`${testId}-cancel`}
        >
          ✕
        </span>
      </div>
      {filtered.length === 0 ? (
        <span style={{ ...mutedStyle, marginTop: 'var(--space-2)' }} data-testid={`${testId}-empty`}>
          {emptyText}
        </span>
      ) : (
        <div style={presultboxStyle} data-testid={`${testId}-results`}>
          {filtered.map((o) => (
            <div style={presultStyle} key={o.id} data-testid={`${testId}-option-${o.id}`}>
              <div style={presultMetaStyle}>
                <div style={presultNmStyle}>{o.label}</div>
              </div>
              <button
                type="button"
                style={miniaddSubStyle}
                onClick={() => {
                  onPick(o.id);
                  setOpen(false);
                  setTerm('');
                }}
                data-testid={`${testId}-pick-${o.id}`}
              >
                Add
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
